import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useGetAllAgencies } from '@urbanx/agx.agencies.client';
import {
  AutomationFailureResponseStatus,
  type AutomationFailureResponse,
  useGetAutomationFailureMetrics,
} from '@urbanx/agx.support.client';
import { AgxBadge, AgxSpacer, type SelectChipOption } from '@urbanx/agx.ui-components2';
import { CampaignTabHeader } from 'components';
import { useAutomationFailureRefresh } from 'hooks/useAutomationFailureRefresh';
import { ContentLayout } from 'layout';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AutomationErrorFilters, type AutomationErrorFilterValues } from './AutomationErrorFilters';
import AutomationErrorMetrics from './AutomationErrorMetrics';
import { AutomationErrorsTable } from './AutomationErrorsTable';
import AutomationFailureDetail from './AutomationFailureDetail';
import {
  buildAutomationFailureListQuery,
  getAutomationFailuresQueryKey,
  mergeAutomationFailurePages,
} from './automationFailureQuery';
import { useAutomationFailureList } from './useAutomationFailureList';

interface AutomationErrorsProps {
  campaignId?: string;
}

const FAILURE_QUERY_PARAMETER = 'failure';
const PAGE_SIZE = 50;

const defaultFilters: AutomationErrorFilterValues = {
  status: AutomationFailureResponseStatus.Unresolved,
  occurredPeriod: 'last7Days',
};

export const AutomationErrors = ({ campaignId }: AutomationErrorsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(defaultFilters);
  const [continuationToken, setContinuationToken] = useState<string>();
  const [loadedFailures, setLoadedFailures] = useState<AutomationFailureResponse[]>([]);
  const [queryNowInstant, setQueryNowInstant] = useState(() => new Date().toISOString());
  const selectedFailureReference = searchParams.get(FAILURE_QUERY_PARAMETER) ?? undefined;
  const query = buildAutomationFailureListQuery(filters, campaignId, queryNowInstant);
  const queryKey = getAutomationFailuresQueryKey(query, continuationToken, PAGE_SIZE);
  const {
    data: failurePage,
    isPending: failuresPending,
    isError: failuresError,
  } = useAutomationFailureList(query, continuationToken, PAGE_SIZE);
  const failuresFetching = useIsFetching({ queryKey, exact: true }) > 0;
  const { data: metrics } = useGetAutomationFailureMetrics(campaignId);
  const { data: agencies } = useGetAllAgencies(!campaignId);
  const queryClient = useQueryClient();
  const failures = continuationToken
    ? mergeAutomationFailurePages(loadedFailures, failurePage?.items ?? [])
    : (failurePage?.items ?? []);

  const agencyOptions: SelectChipOption[] | undefined = campaignId
    ? undefined
    : (agencies ?? []).flatMap((agency) =>
        agency.id && agency.name ? [{ value: agency.id, label: agency.name }] : []
      );

  const resetPagination = () => {
    setLoadedFailures([]);
    setContinuationToken(undefined);
    setQueryNowInstant(new Date().toISOString());
  };

  useAutomationFailureRefresh(resetPagination);

  const changeFilters = (patch: Partial<AutomationErrorFilterValues>) => {
    resetPagination();
    setFilters((current) => ({ ...current, ...patch }));
  };

  const setSelectedFailure = (reference?: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (reference) {
      nextSearchParams.set(FAILURE_QUERY_PARAMETER, reference);
    } else {
      nextSearchParams.delete(FAILURE_QUERY_PARAMETER);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const selectFailure = (failure: AutomationFailureResponse) => {
    if (failure.reference) setSelectedFailure(failure.reference);
  };

  const loadMore = () => {
    if (failuresFetching) return;

    if (continuationToken && failuresError) {
      void queryClient.refetchQueries({ queryKey, exact: true });
      return;
    }

    if (failurePage?.continuationToken) {
      setLoadedFailures(failures);
      setContinuationToken(failurePage.continuationToken);
    }
  };

  const errorsTable = (
    <AutomationErrorsTable
      failures={failures}
      isPending={failuresPending}
      onSelectFailure={selectFailure}
      showCampaign={!campaignId}
      emptyLabel={
        campaignId
          ? 'No automation errors have been recorded for this campaign.'
          : 'No automation errors match these filters.'
      }
      hasMore={
        !!failurePage?.continuationToken ||
        (!!continuationToken && (failuresPending || failuresError))
      }
      onLoadMore={loadMore}
      isLoadingMore={!!continuationToken && failuresFetching}
    />
  );

  return (
    <AgxSpacer
      direction="vertical"
      fill
      gap={campaignId ? 'spacing-md' : 'spacing-xl'}
      style={{ flex: 1, minHeight: 0, width: '100%' }}
    >
      {campaignId ? (
        <CampaignTabHeader
          title={
            metrics?.totalUnresolved === undefined ? (
              'Automation Errors'
            ) : (
              <AgxSpacer direction="horizontal" contentAlignment="center" gap="xs">
                Automation Errors
                <AgxBadge
                  type="colourfill"
                  colour="lightgrey"
                  size="md"
                  text={`${metrics.totalUnresolved} unresolved`}
                />
              </AgxSpacer>
            )
          }
          titleId="automation-errors-heading"
        />
      ) : null}

      {!campaignId && <AutomationErrorMetrics metrics={metrics} />}

      {!campaignId && (
        <AutomationErrorFilters
          value={filters}
          agencyOptions={agencyOptions}
          onChange={changeFilters}
        />
      )}

      {campaignId ? (
        <AgxSpacer
          direction="vertical"
          gap="spacing-xl"
          padding={{ top: 'spacing-xs', bottom: 'spacing-4xl' }}
          fill
        >
          {errorsTable}
        </AgxSpacer>
      ) : (
        <ContentLayout hasSecondaryMenu={false}>{errorsTable}</ContentLayout>
      )}

      {selectedFailureReference && (
        <AutomationFailureDetail
          reference={selectedFailureReference}
          onClose={() => setSelectedFailure()}
          onFailureChanged={resetPagination}
        />
      )}
    </AgxSpacer>
  );
};
